import React, { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';
import _get from 'lodash/get';
import { Site } from 'graphql-common';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import useModalManagement from '@lib/hooks/useModalManagement';
import Modal from '@lib/components/Modal/Modal';
import Form, { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import TasksTimelineFilterActions from 'routes/Dashboard/TasksTimeline/TasksTimelineFilterActions';
import formStyles from '@lib/assets/styles/Form.module.scss';
import useSiteListFilterFields, {
  SITE_LIST_FILTER_EMPTY_VALUES,
} from 'routes/Sites/utils/useSiteListFilterFields';
import useSiteListActions from 'routes/Sites/utils/useSiteListActions';
import { Modals } from '@lib/enums/modals';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import Map from '@lib/components/Map/Map';
import { MapParams } from '@lib/components/Map/types';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import Search from '@lib/components/Search/Search';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import SitePreview from 'components/SitePreview/SitePreview';
import Pagination from '@lib/components/Pagination/Pagination';
import EmptyState from '@lib/components/EmptyState/EmptyState';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import CircleLoaderWrapper, {
  CircleLoaderWrapperType,
} from '@lib/components/CircleLoaderWrapper/CircleLoaderWrapper';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import { IconName } from '@lib/components/Modal/enums';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import { APP_URLS } from 'constants/urls';
import SitesFilterFieldNames from '@lib/enums/fieldNames/sitesFilterFieldNames';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import TableIcon from '@lib/assets/icons/view_list_icon.svg?react';
import { SiteListView } from 'routes/Sites/enums';
import Table from '@lib/components/Table/Table';
import getSitesTableActionsMenu from 'routes/Sites/utils/getSitesTableActionsMenu';
import getSitesTableColumns from 'routes/Sites/utils/getSitesTableColumns';
import { useTableItemsBulkSelect } from '@lib/hooks/useTableItemsBulkSelect';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import {
  SITE_LIST_VIEW,
  SITE_MAP_VIEW_PARAMS,
} from '@lib/enums/localStorageKeys';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import Trans from '@lib/components/Trans/Trans';
import { MapSize } from '@lib/components/Map/enums';
import removeTypenameDeep from '@lib/utils/removeTypenameDeep';
import { handleCenterMap } from '@lib/utils/mapUtils';
import SiteMapPopupContentAsyncWrap from 'components/SiteMapPopupContent/SiteMapPopupContentAsyncWrap';
import styles from './SiteListContainer.module.scss';

const modals = [Modals.SiteListFilter, Modals.SiteDestroyModal];

function SiteListContainer() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const mapRef = useRef<L.Map>(null);
  const [mapParams, setMapParams] = useLocalStorage<MapParams>(
    SITE_MAP_VIEW_PARAMS,
    null,
  );
  const [view, setView] = useLocalStorage(SITE_LIST_VIEW, SiteListView.Grid);
  const formRef = useRef<FormRef>(undefined);
  const useBulkSelectResult = useTableItemsBulkSelect<Site>();
  const useModalManagementResult = useModalManagement(modals);
  const { isModalOpened, closeModal } = useModalManagementResult;

  const {
    clearFilter,
    closeFilter,
    filterParams,
    isEmptyState,
    isEmptyStateByFilter,
    isEmptyStateVisible,
    isPaginationVisible,
    isViewOnly,
    onConfirmedDelete,
    onDeleteHandler,
    onEditHandler,
    onFilterSubmit,
    onViewHandler,
    openFilter,
    resetSearchQuery,
    sectorsLoadOptions,
    siteLocationsQueryHookResult,
    sitesQueryHookResult,
    siteLocationCenterQueryHookResult,
  } = useSiteListActions({
    view,
    mapParams,
    useModalManagementResult,
    useBulkSelectResult,
  });

  const {
    data: siteLocationCenter,
    firstLoading: siteLocationCenterLoading,
    refetch: siteLocationCenterRefetch,
  } = siteLocationCenterQueryHookResult;

  const onCenterMap = () => {
    siteLocationCenterRefetch().then((res) => {
      handleCenterMap({ res, mapRef });
    });
  };

  const newCenter =
    siteLocationCenter?.lon && siteLocationCenter?.lat
      ? { lng: siteLocationCenter.lon, lat: siteLocationCenter.lat }
      : null;

  const filterCounter = _get(
    filterParams,
    [SitesFilterFieldNames.Sectors, 'length'],
    0,
  );

  const tableColumns = getSitesTableColumns({
    onViewHandler,
    t,
  });
  const tableActionsMenu = getSitesTableActionsMenu({
    onEditHandler,
    onViewHandler,
    onDeleteHandler,
    isViewOnly,
    t,
  });
  const filterFields = useSiteListFilterFields({
    sectorsLoadOptions,
  });

  const defaultFilterValues =
    filterParams && Object.keys(filterParams).length > 0
      ? filterParams
      : SITE_LIST_FILTER_EMPTY_VALUES;

  if (sitesQueryHookResult.error) {
    return (
      <ErrorPage
        status={getStatusFromError(sitesQueryHookResult.error)}
        description={sitesQueryHookResult.error.message}
      />
    );
  }

  if (siteLocationsQueryHookResult.error) {
    return (
      <ErrorPage
        status={getStatusFromError(siteLocationsQueryHookResult.error)}
        description={siteLocationsQueryHookResult.error.message}
      />
    );
  }

  const mapData = siteLocationsQueryHookResult.data
    ? removeTypenameDeep(siteLocationsQueryHookResult.data)
    : undefined;

  const emptyState = (
    <div>
      {isEmptyStateByFilter && (
        <EmptyState
          title={t('no-results-found')}
          text={t('no-results-found-text')}
          icon={IconName.SearchOff}
          actions={[
            <Button
              key="back"
              buttonText={t('go-back')}
              buttonType={ButtonTypes.primaryFilled}
              leftIcon="arrow_back"
              onClick={resetSearchQuery}
            />,
          ]}
        />
      )}
      {isEmptyState && (
        <EmptyState
          title={t('no-sites-yet')}
          text={t('no-sites-text')}
          icon={IconName.Cancel}
          actions={
            isViewOnly
              ? []
              : [
                  <Button
                    key="back"
                    buttonText={t('create-site')}
                    buttonType={ButtonTypes.primaryFilled}
                    leftIcon="add"
                    to={APP_URLS.dashboard.sites.add.path}
                  />,
                ]
          }
        />
      )}
    </div>
  );
  const tableEmptyState = (
    <ListEmptyState
      title={t('no-sites-yet')}
      text={t('no-sites-text')}
      actions={
        isViewOnly
          ? []
          : [
              <Button
                key="add"
                buttonText={t('create-new-site')}
                buttonType={ButtonTypes.primaryFilled}
                leftIcon="add"
                to={APP_URLS.dashboard.sites.add.path}
              />,
            ]
      }
    />
  );

  const popupContentRender = (renderProps) => {
    const siteId = _get(renderProps, ['properties', 'sourceables', 0, 'id']);
    return <SiteMapPopupContentAsyncWrap siteId={siteId} {...renderProps} />;
  };

  return (
    <>
      <BreadCrumbs data={getBreadCrumbsDataByKey({ pathname, t })} />
      <PageHeader title={t('sites')}>
        {isViewOnly ? (
          <ViewOnlyChip />
        ) : (
          <Button
            buttonText={t('create-new-site')}
            leftIcon="add"
            to={APP_URLS.dashboard.sites.add.path}
          />
        )}
      </PageHeader>
      <PageBody className={styles.filterWrap}>
        <WhiteBox
          className={styles.filter}
          verticalPaddings="extraSmall"
          horizontalPaddings="small"
        >
          <div className={styles.filterRow}>
            <div>
              <Chip
                counter={filterCounter}
                label={t('filter')}
                leftIcon="filter_alt"
                leftIconSymbolsOutlined
                onClick={openFilter}
                size={ChipSizes.large}
                type={ChipTypes.chipFilter}
                tooltipForCroppedTextDisabled
              />
            </div>
            {!!filterCounter && (
              <div>
                <Button
                  buttonSize={ButtonSizes.large}
                  buttonText={t('reset')}
                  buttonType={ButtonTypes.primaryText}
                  onClick={clearFilter}
                />
              </div>
            )}
          </div>
          <div className={styles.viewChangeActions}>
            <Tooltip body={t('list-view')}>
              <IconButton
                icon="grid_view"
                onClick={() => setView(SiteListView.Grid)}
                type={
                  view === SiteListView.Grid
                    ? IconButtonTypes.PrimaryFilled
                    : IconButtonTypes.PrimaryOutlined
                }
              />
            </Tooltip>
            <Tooltip body={t('table-view')}>
              <IconButton
                svgIcon={<TableIcon />}
                onClick={() => setView(SiteListView.Table)}
                type={
                  view === SiteListView.Table
                    ? IconButtonTypes.PrimaryFilled
                    : IconButtonTypes.PrimaryOutlined
                }
              />
            </Tooltip>
            <Tooltip body={t('map-view')}>
              <IconButton
                icon="map"
                symbolsOutlined
                onClick={() => setView(SiteListView.Map)}
                type={
                  view === SiteListView.Map
                    ? IconButtonTypes.PrimaryFilled
                    : IconButtonTypes.PrimaryOutlined
                }
              />
            </Tooltip>
          </div>
        </WhiteBox>
      </PageBody>
      <PageBody>
        <>
          <div>
            {!isEmptyState && (
              <Search placeholder={t('sites-search-placeholder')} />
            )}
          </div>
          {view === SiteListView.Map && (
            <div>
              {siteLocationCenterLoading ? (
                <CircleLoader />
              ) : (
                <Map
                  defaultCenter={newCenter}
                  disableAutoFit
                  mapParams={mapParams}
                  mapRef={mapRef}
                  onCenterMap={onCenterMap}
                  PopupContent={popupContentRender}
                  setMapParams={setMapParams}
                  size={MapSize.Large}
                  value={mapData}
                />
              )}
            </div>
          )}
          {(view === SiteListView.Grid || view === SiteListView.Table) && (
            <div>
              {isEmptyStateVisible ? (
                emptyState
              ) : (
                <div>
                  {sitesQueryHookResult.firstLoading ? (
                    <CircleLoader />
                  ) : (
                    <CircleLoaderWrapper
                      isLoading={sitesQueryHookResult.loading}
                      type={CircleLoaderWrapperType.blur}
                    >
                      {view === SiteListView.Grid && (
                        <>
                          <div className={styles.sitesGrid}>
                            {sitesQueryHookResult.collection.map((site) => (
                              <div key={site.id}>
                                <SitePreview {...site} />
                              </div>
                            ))}
                          </div>
                          {isPaginationVisible && (
                            <div className={styles.pagination}>
                              <Pagination
                                totalEntries={sitesQueryHookResult.totalCount}
                              />
                            </div>
                          )}
                        </>
                      )}
                      {view === SiteListView.Table && (
                        <Table
                          actionsMenu={tableActionsMenu}
                          columns={tableColumns}
                          counterText={t('site-plural', {
                            count: sitesQueryHookResult.totalCount,
                          })}
                          data={sitesQueryHookResult.collection}
                          emptyState={tableEmptyState}
                          fullHeight
                          id="sectors"
                          loading={sitesQueryHookResult.loading}
                          totalEntries={sitesQueryHookResult.totalCount}
                        />
                      )}
                    </CircleLoaderWrapper>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      </PageBody>
      <Modal
        isOpen={isModalOpened(Modals.SiteListFilter)}
        caption={t('filter')}
        onClose={closeFilter}
      >
        <Form
          formRef={formRef}
          fields={filterFields}
          onSubmit={onFilterSubmit}
          defaultValues={defaultFilterValues}
          actionsComponent={TasksTimelineFilterActions}
          actionsComponentProps={{
            onCancel: closeFilter,
          }}
          formClassName={formStyles.form}
        />
      </Modal>
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="delete-site-text" />}
        icon={IconName.Delete}
        isOpen={isModalOpened(Modals.SiteDestroyModal)}
        onClose={() => closeModal(Modals.SiteDestroyModal)}
        onConfirm={onConfirmedDelete}
        title={t('are-you-sure')}
      />
    </>
  );
}

export default SiteListContainer;
